import React, { Suspense, lazy } from 'react';
import { Navigate } from 'react-router-dom';
import ROUTES from '../constants/routes';
import MasterLayout from '../components/layouts/MasterLayout';
import RoleBasedRoute from '../components/common/RoleBasedRoute';
import ProtectedRoute from '../components/common/ProtectedRoute';

// Loading component for suspense fallback
const LoadingFallback = () => (
  <div className="flex items-center justify-center h-full min-h-[200px]">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
  </div>
);

// Lazy load components
const LoginPage = lazy(() => import('../pages/auth/LoginPage'));
const DashboardPage = lazy(() => import('../pages/dashboard/DashboardPage'));
const CustomersPage = lazy(() => import('../pages/customers/CustomersPage'));
const CustomerDetailPage = lazy(() => import('../pages/customers/CustomerDetailPage'));
const NewCustomerPage = lazy(() => import('../pages/customers/NewCustomerPage'));
const EditCustomerPage = lazy(() => import('../pages/customers/EditCustomerPage'));
const EmployeesPage = lazy(() => import('../pages/employees/EmployeesPage'));
const NewEmployeePage = lazy(() => import('../pages/employees/NewEmployeePage'));
const EditEmployeePage = lazy(() => import('../pages/employees/EditEmployeePage'));
const ProfilePage = lazy(() => import('../pages/employees/ProfilePage'));
const OrdersPage = lazy(() => import('../pages/orders/OrdersPage'));
const OrderDetailPage = lazy(() => import('../pages/orders/OrderDetailPage'));
const NewOrderPage = lazy(() => import('../pages/orders/NewOrderPage'));
const EditOrderPage = lazy(() => import('../pages/orders/EditOrderPage'));
const AdvancedDeliveryPage = lazy(() => import('../pages/operations/AdvancedDeliveryPage'));
const SettingsPage = lazy(() => import('../pages/settings/SettingsPage'));
const NotificationsPage = lazy(() => import('../pages/notifications/NotificationsPage'));
const DeliveryReportPage = lazy(() => import('../pages/reports/DeliveryReportPage'));
const DeliveryAdminReportPage = lazy(() => import('../pages/reports/DeliveryAdminReportPage'));
const RouteAnalysisPage = lazy(() => import('../pages/reports/RouteAnalysisPage'));
const RankingReportPage = lazy(() => import('../pages/reports/RankingReportPage'));
const UpdatesPage = lazy(() => import('../pages/updates/UpdatesPage'));
const NewUpdatePage = lazy(() => import('../pages/updates/NewUpdatePage'));
const NotFoundPage = lazy(() => import('../pages/utility/NotFoundPage'));

// Helper function to wrap components in Suspense
const withSuspense = (Component: React.LazyExoticComponent<React.ComponentType>) => {
  return (
    <Suspense fallback={<LoadingFallback />}>
      <Component />
    </Suspense>
  );
};

// Helper function for admin-only routes
const adminOnly = (Component: React.LazyExoticComponent<React.ComponentType>) => {
  return (
    <RoleBasedRoute allowedRoles={['admin']}>
      {withSuspense(Component)}
    </RoleBasedRoute>
  );
};

const routes = [
  {
    path: ROUTES.LOGIN,
    element: withSuspense(LoginPage),
  },
  {
    path: '/',
    element: <ProtectedRoute><MasterLayout /></ProtectedRoute>,
    children: [
      {
        path: '/',
        element: (
          <RoleBasedRoute allowedRoles={['admin']}>
            <Navigate to={ROUTES.DASHBOARD} replace />
          </RoleBasedRoute>
        ),
      },
      // Default route for non-admin users
      {
        path: '/default',
        element: <Navigate to={ROUTES.ADVANCED_DELIVERY} replace />,
      },
      // Open to runners as well as admins: the backend scopes the charts by role, so
      // a runner sees only their own deliveries. The `/` index route above still sends
      // them to Advanced Delivery on login; they reach the dashboard from the menu.
      {
        path: ROUTES.DASHBOARD,
        element: withSuspense(DashboardPage),
      },
      // Customers - Admin only
      {
        path: ROUTES.CUSTOMERS,
        element: adminOnly(CustomersPage),
      },
      {
        path: ROUTES.CUSTOMER_DETAIL,
        element: adminOnly(CustomerDetailPage),
      },
      {
        path: ROUTES.CUSTOMER_NEW,
        element: adminOnly(NewCustomerPage),
      },
      {
        path: ROUTES.CUSTOMER_EDIT,
        element: adminOnly(EditCustomerPage),
      },
      // Orders
      {
        path: ROUTES.ORDERS,
        element: adminOnly(OrdersPage),
      },
      {
        path: ROUTES.ORDER_DETAIL,
        element: adminOnly(OrderDetailPage),
      },
      {
        path: ROUTES.ORDER_NEW,
        element: adminOnly(NewOrderPage),
      },
      {
        path: ROUTES.ORDER_EDIT,
        element: adminOnly(EditOrderPage),
      },
      // Delivery
      {
        path: ROUTES.ADVANCED_DELIVERY,
        element: withSuspense(AdvancedDeliveryPage),
      },
      // Employees - Admin only
      {
        path: ROUTES.EMPLOYEES,
        element: adminOnly(EmployeesPage),
      },
      {
        path: ROUTES.EMPLOYEE_NEW,
        element: adminOnly(NewEmployeePage),
      },
      {
        path: ROUTES.EMPLOYEE_EDIT,
        element: adminOnly(EditEmployeePage),
      },
      // Profile
      {
        path: ROUTES.PROFILE,
        element: withSuspense(ProfilePage),
      },
      // Settings - Admin only
      {
        path: ROUTES.SETTINGS,
        element: adminOnly(SettingsPage),
      },
      // Notifications
      {
        path: ROUTES.NOTIFICATIONS,
        element: withSuspense(NotificationsPage),
      },
      // Reports
      {
        path: ROUTES.DELIVERY_REPORT,
        element: withSuspense(DeliveryReportPage),
      },
      {
        path: ROUTES.DELIVERY_ADMIN_REPORT,
        element: adminOnly(DeliveryAdminReportPage),
      },
      {
        path: ROUTES.ROUTE_ANALYSIS_REPORT,
        element: adminOnly(RouteAnalysisPage),
      },
      {
        path: ROUTES.RANKING_REPORT,
        element: withSuspense(RankingReportPage),
      },
      // Updates - Admin only
      {
        path: ROUTES.UPDATES,
        element: adminOnly(UpdatesPage),
      },
      {
        path: ROUTES.UPDATE_NEW,
        element: adminOnly(NewUpdatePage),
      },
      // Not found
      {
        path: '404',
        element: withSuspense(NotFoundPage),
      },
      {
        path: '*',
        element: <Navigate to="/404" replace />,
      },
    ],
  },
];

export default routes;